/**
 * bankProofDocs - which company bank proof on a user's document list is the
 * current one (corporate Business PAD contract, Amendment A3, 2026-10-25).
 *
 * Proof rows are retired, never deleted: when the company sends a new void
 * cheque or bank letter, the API sets the earlier corporate_bank_proof rows to
 * status 0 (an instrument version keeps the proof it was captured or verified
 * with), and admin get-user-details-byid `documents` lists them all. So the
 * list can hold several proofs, and only one of them is current.
 *
 * The current proof is the row the API treats as current
 * (investorBankCapture.proofOnFileEvidence): status 1 on the CORPORATE side,
 * the newest (highest id) when there is more than one. Every other
 * corporate_bank_proof row is labelled a superseded proof. A status 1 proof on
 * the individual side is not the company's current proof. Other document
 * types are not labelled.
 */

export const PROOF_DOC_TITLE = "corporate_bank_proof";
export const CURRENT_PROOF_LABEL = "Current proof";
export const SUPERSEDED_PROOF_LABEL = "Superseded proof";

const isProof = (doc) => !!doc && doc.doc_title === PROOF_DOC_TITLE;
const isLive = (doc) =>
  Number(doc.status) === 1 && String(doc.user_type || "").trim().toUpperCase() === "CORPORATE";
// `later` comes after `earlier` in the list: newer by id, or by list order
// when either id is not a number.
const isNewer = (later, earlier) => {
  const a = Number(later.id);
  const b = Number(earlier.id);
  return Number.isFinite(a) && Number.isFinite(b) ? a > b : true;
};

/**
 * -> one label per document, in the list's order: CURRENT_PROOF_LABEL,
 * SUPERSEDED_PROOF_LABEL, or null for a document that is not a company bank
 * proof.
 */
export function proofStatusLabels(documents) {
  const docs = Array.isArray(documents) ? documents : [];
  let current = -1;
  docs.forEach((doc, index) => {
    if (!isProof(doc) || !isLive(doc)) return;
    if (current === -1 || isNewer(doc, docs[current])) current = index;
  });
  return docs.map((doc, index) => {
    if (!isProof(doc)) return null;
    return index === current ? CURRENT_PROOF_LABEL : SUPERSEDED_PROOF_LABEL;
  });
}
